import { object, string } from 'yup';
import {
  useAddWorkflowConfiguration,
  useGetAnnotationValidation,
  useGetConfigurationLabels,
  useGetWorkflowDetail,
  useListInferenceModels,
  useUpdateWorkflowConfiguration
} from 'api/sdk';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import LabelWorkflow from './LabelWorkFlow';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Typography } from 'components';
import TrainSummaryModal from './TrainSummaryModal';
import {
  clearHistory,
  setAnnotationData,
  setFrameIndex,
  setObjectMapping,
  setWorkflowConfig
} from 'store/annotations/annotationSlice';
import { useWorkflow } from 'hooks/config/useWorkflow';
import { useSnack } from 'plugins/snack';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface WorkflowLabel {
  id: string;
  label_id: string;
  label: string;
  dependency: string[] | null;
  mandatory: boolean;
  start_time: string;
  end_time: string;
}

const WorkFlowConfig = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const modelId = searchParams.get('modelId');
  const method = searchParams.get('method');
  const workflowId = searchParams.get('workflowId');
  const currentConfigId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const { refetch } = useListInferenceModels(currentConfigId);
  const objectMapping = useSelector(
    (state: RootState) => state.annotation.objectMapping
  );
  const snack = useSnack();
  const navigate = useNavigate();
  const { mutateAsync: addWorkflow, isLoading: addWorkflowLoading } =
    useAddWorkflowConfiguration();
  const [trainingStarted, setTrainingStarted] = useState(false);
  const [workflow, setWorkflow] = useState<WorkflowLabel[]>([]);
  const [workflowName, setWorkFlowName] = useState('');
  const [open, setOpen] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');
  const { setRows, id: configId, cameras, train, trainLoading } = useWorkflow();
  const { mutate: updateWorkflow, isLoading: updateWorkflowLoading } =
    useUpdateWorkflowConfiguration();
  const selectedVideos = useSelector(
    (state: RootState) => state.configuration.filteredVideo
  );
  const [isSaveModal, setSaveModal] = useState(false);
  const { data: workflowDetails, isLoading: workflowLoading }: any =
    useGetWorkflowDetail(currentConfigId, {
      workflow_id: workflowId
    });
  const { data: validationData } = useGetAnnotationValidation(
    currentConfigId || '',
    selectedVideos.join(',')
  );

  const workflowSchema = object({
    name: string()
      .required('Workflow name is required')
      .min(3, 'Name must be at least 3 characters')
      .max(20, 'Name must not exceed 20 characters')
  });

  const workflowConfig = useSelector(
    (state: RootState) => state.annotation.workFlowConfig
  );

  const { data: getExistingLabels } =
    useGetConfigurationLabels(currentConfigId);

  // Memoize the labels
  const existingLabels = useMemo(
    () => getExistingLabels?.data?.labels || [],
    [getExistingLabels?.data?.labels]
  );

  // Initialize workflow only when necessary data is available
  useEffect(() => {
    if (
      method === 'edit' &&
      workflowDetails?.data?.data?.workflow?.[0] &&
      !workflowLoading
    ) {
      const workflowData = workflowDetails.data.data.workflow[0];

      // Set workflow config
      if (workflowData.workflow_config) {
        const mappedWorkflow: WorkflowLabel[] =
          workflowData.workflow_config.map((item: any) => ({
            id: item.id,
            label_id: item.label_id,
            label: item.label,
            dependency: item.dependency || [],
            mandatory: item.mandatory,
            start_time: item.start_time || '',
            end_time: item.end_time || ''
          }));
        setWorkflow(mappedWorkflow);
        dispatch(setWorkflowConfig(mappedWorkflow));
      }

      // Set object mapping
      if (workflowData.object_mapping) {
        dispatch(setObjectMapping(workflowData.object_mapping));
      }
    } else if (
      existingLabels.length > 0 &&
      workflow.length === 0 &&
      !workflowLoading
    ) {
      // Handle new workflow creation - only initialize once
      const initialWorkflow: WorkflowLabel[] = existingLabels.map(
        (label, index) => ({
          id: `workflow-${label.id}`,
          label_id: label.id,
          label: label.label_name,
          dependency: index === 0 ? null : [],
          mandatory: false,
          start_time: index === 0 ? '00:00' : '',
          end_time: ''
        })
      );
      setWorkflow(initialWorkflow);

      dispatch(setWorkflowConfig(initialWorkflow));
    }
  }, [
    workflowDetails?.data,
    existingLabels,
    method,
    workflowLoading,
    dispatch
  ]);

  console.log('object mapping inside the new method', objectMapping);

  // Handle workflow changes - memoize the function
  const handleWorkflowChange = useCallback(
    (newWorkflow: WorkflowLabel[]) => {
      setWorkflow(newWorkflow);
      dispatch(setWorkflowConfig(newWorkflow));
    },
    [dispatch]
  );

  const handleTrain = useCallback(
    async (model_name: string, workflow_name: string, epochs: number) => {
      console.log(
        'workflowconfig inside the train api',
        model_name,
        workflow_name,
        epochs
      );
      const temp: any = {
        object_mapping: objectMapping,
        workflow_config: workflowConfig
      };
      const finalList = [temp];

      try {
        await train({
          configId: configId || '',
          data: {
            model_name: model_name,
            model_type: 'yolact',
            epochs,
            workflow_name: workflow_name,
            workflow_config: finalList,
            video_ids: selectedVideos,
            annotation_count: validationData?.data?.total_annotations || 0
          }
        });

        setTrainingStarted(true);
        snack({
          message: 'Model training started successfully',
          severity: 'success'
        });

        dispatch(setObjectMapping([]));
        dispatch(setAnnotationData([]));
        dispatch(setWorkflowConfig([]));
        dispatch(setFrameIndex(1));
        dispatch(clearHistory());

        setTimeout(() => {
          navigate('/configuration');
        }, 4000);
      } catch (error: any) {
        console.error('Training error:', error);
        snack({
          message: error?.response?.data?.detail || 'Training failed',
          severity: 'error'
        });
      }
    },
    [
      configId,
      dispatch,
      navigate,
      objectMapping,
      selectedVideos,
      snack,
      train,
      validationData?.data?.total_annotations,
      workflowConfig
    ]
  );

  const handleSave = useCallback(async () => {
    if (!workflowId) return;

    try {
      const temp: any = {
        object_mapping: objectMapping,
        workflow_config: workflowConfig
      };
      const finalList = [temp];

      await updateWorkflow({
        configId: currentConfigId,
        workflowId,
        data: {
          workflow_config: finalList
        }
      });

      snack({
        message: 'Workflow updated successfully',
        severity: 'success'
      });

      dispatch(setWorkflowConfig([]));
      await refetch();
      navigate('/train');
    } catch (error: any) {
      console.error('Update error:', error);
      snack({
        message: error?.response?.data?.detail || 'Failed to update workflow',
        severity: 'error'
      });
    }
  }, [
    currentConfigId,
    dispatch,
    navigate,
    objectMapping,
    refetch,
    snack,
    updateWorkflow,
    workflowConfig,
    workflowId
  ]);
  console.log(
    workflowConfig,
    'workflowConfigworkflowConfigworkflowConfigworkflowConfig'
  );
  const handleSaveAs = useCallback(async () => {
    if (!modelId) return;
    setSaveModal(false);

    try {
      const temp: any = {
        object_mapping: objectMapping,
        workflow_config: workflowConfig
      };
      const finalList = [temp];

      await addWorkflow({
        configId: currentConfigId,
        modelId,
        data: {
          model_id: modelId,
          workflow_config: finalList,
          workflow_name: workflowName
        }
      });

      snack({
        message: 'New workflow created successfully',
        severity: 'success'
      });

      dispatch(setWorkflowConfig([]));
      await refetch();
      navigate('/train');
    } catch (error: any) {
      setValidationError(
        error?.response?.data?.detail || 'Failed to create workflow'
      );
      snack({
        message:
          error?.response?.data?.detail || 'Failed to create new workflow',
        severity: 'error'
      });
      // Reopen modal on error
      setSaveModal(true);
    }
  }, [
    addWorkflow,
    currentConfigId,
    dispatch,
    modelId,
    navigate,
    objectMapping,
    refetch,
    snack,
    workflowConfig,
    workflowName
  ]);

  const handleBack = useCallback(() => {
    if (method) {
      navigate('/train');
    } else {
      navigate('/train/pick_&_place_annotation/activity_mapping');
    }
  }, [method, navigate]);

  const handleInputChange = useCallback(
    (value: string) => {
      setWorkFlowName(value);
      try {
        workflowSchema.validateSync({ name: value });
        setValidationError('');
      } catch (err: any) {
        setValidationError(err.message);
      }
    },
    [workflowSchema]
  );

  const handleStartTraining = useCallback(() => {
    dispatch(setWorkflowConfig(workflow));
    const temp: any = {
      objectMapping: objectMapping,
      objectWorkflow: workflowConfig
    };
    setRows(temp);
    setOpen(true);
  }, [dispatch, objectMapping, setRows, workflow, workflowConfig]);

  return (
    <>
      {workflowLoading ? (
        <div className="flex items-center justify-center w-full h-[70vh]">
          <p>Loading...</p>
        </div>
      ) : (
        <LabelWorkflow
          labels={existingLabels}
          selectedWorkflow={workflow}
          onWorkflowChange={handleWorkflowChange}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 20,
          marginTop: 20
        }}
      >
        <Button onClick={handleBack}>Back</Button>
        {method === 'new' && (
          <Button
            onClick={() => setSaveModal(true)}
            loading={updateWorkflowLoading}
          >
            Save As
          </Button>
        )}
        {method === 'edit' && (
          <>
            <Button onClick={handleSave} loading={updateWorkflowLoading}>
              Save
            </Button>
            <Button
              onClick={() => setSaveModal(true)}
              loading={addWorkflowLoading}
            >
              Save As
            </Button>
          </>
        )}
        {!method && (
          <Button onClick={handleStartTraining}>Start Training</Button>
        )}
      </div>
      {!method && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          title={
            <Typography
              width={'100%'}
              textAlign={'center'}
              style={{ fontWeight: 'bold' }}
            >
              Training Settings
            </Typography>
          }
          size="md"
        >
          <TrainSummaryModal
            config_id={currentConfigId || ''}
            labelsLength={existingLabels.length}
            activities={[]}
            camera_length={cameras?.length || 0}
            workflow_data={[]}
            onSumbit={handleTrain}
            loading={trainLoading}
            trainingStarted={trainingStarted}
            framesAnnotated={validationData?.data?.total_annotations || 0}
            framesExtracted={validationData?.data?.total_frames}
          />
        </Modal>
      )}
      <Modal
        open={isSaveModal}
        onClose={() => setSaveModal(false)}
        title={'Workflow Name'}
        size="sm"
      >
        <input
          type="text"
          onChange={e => handleInputChange(e.target.value)}
          style={{
            display: 'flex',
            justifyContent: 'center',
            border: '0.5px solid black',
            fontSize: '14px',
            padding: '4px',
            backgroundColor: 'transparent',
            width: '100%',
            marginBottom: '10px'
          }}
          placeholder="Please Enter Workflow Name"
        />
        {validationError && (
          <div className="text-red-500 text-xs mt-[-10px] w-full">
            {validationError}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            version="light"
            onClick={() => {
              setSaveModal(false);
              setValidationError('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAs}
            disabled={!workflowName || validationError.length > 0}
          >
            Create
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default WorkFlowConfig;
